import { useEffect, useState } from "react";
import { Plane, MapPin, Trash2, Loader2, Plus, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Section } from "./ProfileFieldPrimitives";

/**
 * TripsSection — inline "Trips" panel for the profile editor.
 *
 * Loads the current user's upcoming trips, lets them add / delete inline.
 * Purposefully lightweight — a full multi-step trip planner is a follow-up.
 * Location for a new trip defaults to the browser's geolocation (with a
 * paste-a-lat/lng escape hatch) so the Nearby "Visitors" query lights up
 * immediately.
 */
export default function TripsSection() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    destination_city: "",
    destination_lat: "",
    destination_lng: "",
    start_date: "",
    end_date: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trips/me");
      setTrips(data || []);
    } catch {
      toast.error("Could not load trips.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => setForm((f) => ({
        ...f,
        destination_lat: String(p.coords.latitude.toFixed(4)),
        destination_lng: String(p.coords.longitude.toFixed(4)),
      })),
      () => toast.error("Could not get your location."),
      { enableHighAccuracy: false, timeout: 5000 }
    );
  };

  const save = async () => {
    if (saving) return;
    const { destination_city, destination_lat, destination_lng, start_date, end_date, note } = form;
    if (!destination_city.trim() || !destination_lat || !destination_lng || !start_date || !end_date) {
      toast.error("Fill destination, coords, and dates.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/trips", {
        destination_city: destination_city.trim(),
        destination_lat: parseFloat(destination_lat),
        destination_lng: parseFloat(destination_lng),
        start_date,
        end_date,
        note: note.trim() || null,
      });
      toast.success("Trip added — Social Meet will surface it while you're there.");
      setAdding(false);
      setForm({ destination_city: "", destination_lat: "", destination_lng: "", start_date: "", end_date: "", note: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add trip.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    try {
      await api.delete(`/trips/${id}`);
      setTrips((cur) => cur.filter((t) => t.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete.");
    }
  };

  return (
    <Section icon={Plane} title="Trips">
      {loading ? (
        <div className="flex items-center justify-center py-6 text-zinc-500">
          <Loader2 size={14} className="animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          {trips.length === 0 && !adding && (
            <div className="text-sm text-zinc-500 px-4 py-4">
              No trips planned. Add one so people know you're visiting.
            </div>
          )}
          {trips.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between px-4 py-3 gap-3"
              data-testid={`trip-row-${t.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <MapPin size={14} className="text-rose-500 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[15px] text-white truncate">{t.destination_city}</div>
                  <div className="text-[11px] text-zinc-500">
                    {t.start_date} → {t.end_date}
                    {t.note ? ` · ${t.note}` : ""}
                  </div>
                </div>
              </div>
              <button
                onClick={() => remove(t.id)}
                className="text-zinc-500 hover:text-rose-500"
                data-testid={`trip-delete-${t.id}`}
                title="Delete trip"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          {adding ? (
            <div className="px-4 py-4 space-y-3" data-testid="trip-add-form">
              <input
                data-testid="trip-city-input"
                placeholder="Where to? (e.g. Miami, FL)"
                value={form.destination_city}
                onChange={(e) => setForm((f) => ({ ...f, destination_city: e.target.value }))}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  data-testid="trip-lat-input"
                  placeholder="Lat"
                  value={form.destination_lat}
                  onChange={(e) => setForm((f) => ({ ...f, destination_lat: e.target.value }))}
                  className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
                />
                <input
                  data-testid="trip-lng-input"
                  placeholder="Lng"
                  value={form.destination_lng}
                  onChange={(e) => setForm((f) => ({ ...f, destination_lng: e.target.value }))}
                  className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
                />
              </div>
              <button
                type="button"
                onClick={useMyLocation}
                className="text-xs text-rose-400 hover:text-rose-300"
                data-testid="trip-use-my-location"
              >
                Use my current location
              </button>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Start</label>
                  <input
                    data-testid="trip-start-input"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">End</label>
                  <input
                    data-testid="trip-end-input"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
                  />
                </div>
              </div>
              <input
                data-testid="trip-note-input"
                placeholder="Optional note (e.g. 'DM me — down to meet up')"
                maxLength={200}
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="trip-save-button"
                  className="bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-black font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-2"
                >
                  {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Calendar size={14} /> Add trip</>}
                </button>
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  className="text-sm text-zinc-400 hover:text-white"
                  data-testid="trip-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm text-rose-400 hover:text-rose-300 hover:bg-white/[0.02]"
              data-testid="trip-add-button"
            >
              <Plus size={14} /> Add a Trip
            </button>
          )}
        </>
      )}
    </Section>
  );
}
